import { useState, useEffect } from "react";
import { useQuery } from '@tanstack/react-query';

import axios from "axios";
import config from '../../config';
import dayjs from "dayjs";

import '../../styles/announcement.css';
import '../../styles/pagination.css';

import PageHeader from '../../components/general/pageHeader.js';
import AnnouncementCard from "../../components/cards/announcementCard.js";
import DateRangePicker from "../../components/general/dateRangePicker";
import Loading from "../../components/general/loading.js";
import useAuthenticate from '../../components/authentication/authenticate';

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const RAnnouncements = () => {
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 9;
  
  const { isLoading } = useAuthenticate();


  const { data: properties } = useQuery({
    queryKey: ["properties"],
    queryFn: fetchProperties,
  });

  const { data: announcements = [], isLoading: isCardsLoading } = useQuery({
    queryKey: ["announcements", selectedProperty?.value],
    queryFn: () => fetchAnnouncementsByProperty(selectedProperty?.value),
    enabled: !!selectedProperty?.value,
  });

  useEffect(() => {
    if (properties?.length > 0 && !selectedProperty) {
      setSelectedProperty(properties[0]);
    }
  }, [properties, selectedProperty]);

  const filteredAnnouncements = announcements
    ?.filter((a) => {
      const matchesProperty = !selectedProperty || a.property?._id === selectedProperty?.value;
      const announcementDate = dayjs(a.createdAt);
      const matchesDate =
        (!startDate || announcementDate.isAfter(dayjs(startDate).subtract(1, "day"))) &&
        (!endDate || announcementDate.isBefore(dayjs(endDate).add(1, "day")));
      return matchesProperty && matchesDate;
    })
    ?.sort((a, b) => dayjs(b.createdAt).diff(dayjs(a.createdAt))) || [];

  const totalResults = filteredAnnouncements.length;
  const totalPages = Math.ceil(totalResults / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedAnnouncements = filteredAnnouncements.slice( startIndex, startIndex + rowsPerPage );

  const maxVisibleButtons = 10;
  const startPage = Math.floor((currentPage - 1) / maxVisibleButtons) * maxVisibleButtons + 1;
  const endPage = Math.min(startPage + maxVisibleButtons - 1, totalPages);

  return (
    <div className="w-full full-page">
      <PageHeader pageName="Announcements" />
      {isLoading && !selectedProperty ? (
        <Loading />
      ) : (
        <>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-4 border-b-[1.5px] border-[#E8E8E8] pb-[15px] mt-[20px]">
            <div className="w-full md:w-[350px] md:flex-shrink-0">
              <Autocomplete
                options={properties}
                value={selectedProperty}
                onChange={(e, newVal) => {
                  if (newVal && newVal.value && newVal.label) {
                    setSelectedProperty(newVal);
                    setCurrentPage(1);
                  } else {
                    setSelectedProperty(null);
                  }
                }}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : option?.label || option?.name || ''
                }
                renderInput={params => (
                  <TextField {...params} label="Select Property" variant="outlined" fullWidth />
                )}
                sx={{
                  width: "100%",
                  '@media (min-width: 768px)': { width: "350px" },
                }}
              />
            </div>

            <div className="w-full md:w-auto md:flex-1 md:max-w-[475px] md:ml-auto">
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                startLabel="From"
                endLabel="To"
              />
            </div>
          </div>
          {isCardsLoading ? (
            <Loading />
          ) : (
            <div className="page-content">
              <div className="table-wrapper">
                {paginatedAnnouncements.length === 0 ? (
                  <p className="info-text" style={{ marginTop: "-10px" }}>
                    No announcements available.
                  </p>
                ) : (
                  <>
                    {/* 📄 Cards */}
                    <div className="announcement-card-container">
                      {paginatedAnnouncements.map((a, index) => (
                        <AnnouncementCard key={a._id} announcement={a} index={index} />
                      ))}
                    </div>

                    {/* 📄 Pagination */}
                    {totalPages > 1 && (
                      <div className="pagination-controls">
                        <div className="pagination-buttons">
                          <button
                            className="pagination-button"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                          >
                            <i className="fas fa-chevron-left"></i>
                          </button>

                          {startPage > 1 && (
                            <button className="pagination-page" onClick={() => setCurrentPage(startPage - 1)}>
                              ...
                            </button>
                          )}

                          {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                            const pageNum = startPage + i;
                            return (
                              <button
                                key={pageNum}
                                className={`pagination-page ${
                                  currentPage === pageNum ? "active" : ""
                                }`}
                                onClick={() => setCurrentPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            );
                          })}

                          {endPage < totalPages && (
                            <button className="pagination-page" onClick={() => setCurrentPage(endPage + 1)}>
                              ...
                            </button>
                          )}

                          <button
                            className="pagination-button"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                          >
                            <i className="fas fa-chevron-right"></i>
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const fetchAnnouncementsByProperty = async (propertyId) => {
  if (!propertyId) return [];
  try {
    const response = await axios.get(`${config.API_URL}/v2/announcement`, {
      headers: {Authorization: `Bearer ${localStorage.getItem('accessToken')}`},
      params: { property: propertyId, sortBy: "createdAt", sortOrder: "desc"},
    });
    return response.data.results || response.data;
  } catch (err) {
    console.error("❌ Error fetching announcements:", err);
    return [];
  }
};

const fetchProperties = async () => {
  const response = await axios.get(`${config.API_URL}/v2/property`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
  });
  return response.data.map(item => ({
    value: item._id,
    label: item.name,
  }));
};

export default RAnnouncements;
